import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import {
  CarrierAccountType,
  ICarrierAccount,
  RedoCarrierName,
} from "@redotech/redo-model/carrier-account/carrier-account";
import { ICreateCarrierAccount } from "@redotech/redo-model/carrier-account/create-carrier-account";
import { RateProvider } from "@redotech/redo-model/outbound-labels/outbound-labels";
import { toast } from "@redotech/redo-web/alert";
import { Card } from "@redotech/redo-web/card";
import { amazonCarrierDefault } from "@redotech/redo-web/fulfillment/setting/carrier-accounts/carrier-forms/create-amazon-carrier-form";
import { apcCarrierDefault } from "@redotech/redo-web/fulfillment/setting/carrier-accounts/carrier-forms/create-apc-carrier-form";
import { asendiaCarrierDefault } from "@redotech/redo-web/fulfillment/setting/carrier-accounts/carrier-forms/create-asendia-carrier-form";
import { australiaPostCarrierDefault } from "@redotech/redo-web/fulfillment/setting/carrier-accounts/carrier-forms/create-australia-post-carrier-form";
import { canadaPostCarrierDefault } from "@redotech/redo-web/fulfillment/setting/carrier-accounts/carrier-forms/create-canada-post-carrier-form";
import { canparCarrierDefault } from "@redotech/redo-web/fulfillment/setting/carrier-accounts/carrier-forms/create-canpar-carrier-form";
import { dhlCarrierDefault } from "@redotech/redo-web/fulfillment/setting/carrier-accounts/carrier-forms/create-dhl-carrier-form";
import { dhlExpressCarrierDefault } from "@redotech/redo-web/fulfillment/setting/carrier-accounts/carrier-forms/create-dhl-express-carrier-form";
import { EPostGlobalCarrierDefault } from "@redotech/redo-web/fulfillment/setting/carrier-accounts/carrier-forms/create-epost-global-carrier-form";
import { ETAExpressCarrierDefault } from "@redotech/redo-web/fulfillment/setting/carrier-accounts/carrier-forms/create-eta-express-carrier-form";
import { fedexCarrierDefault } from "@redotech/redo-web/fulfillment/setting/carrier-accounts/carrier-forms/create-fedex-carrier-form";
import { fedexCrossBorderCarrierDefault } from "@redotech/redo-web/fulfillment/setting/carrier-accounts/carrier-forms/create-fedex-international-carrier-form";
import { fedexSmartpostCarrierDefault } from "@redotech/redo-web/fulfillment/setting/carrier-accounts/carrier-forms/create-fedex-smartpost-carrier-form";
import { firstmileCarrierDefault } from "@redotech/redo-web/fulfillment/setting/carrier-accounts/carrier-forms/create-first-mile-carrier-form";
import { glsCarrierDefault } from "@redotech/redo-web/fulfillment/setting/carrier-accounts/carrier-forms/create-gls-carrier-form";
import { OnTracCarrierDefault } from "@redotech/redo-web/fulfillment/setting/carrier-accounts/carrier-forms/create-ontrac-carrier-form";
import { PassportGlobalCarrierDefault } from "@redotech/redo-web/fulfillment/setting/carrier-accounts/carrier-forms/create-passport-global-carrier-form";
import { purolatorCarrierDefault } from "@redotech/redo-web/fulfillment/setting/carrier-accounts/carrier-forms/create-purolator-carrier-form";
import { royalMailCarrierDefault } from "@redotech/redo-web/fulfillment/setting/carrier-accounts/carrier-forms/create-royal-mail-carrier-form";
import { royalMailV3CarrierDefault } from "@redotech/redo-web/fulfillment/setting/carrier-accounts/carrier-forms/create-royal-mail-v3-carrier-form";
import { sekoOmniParcelCarrierDefault } from "@redotech/redo-web/fulfillment/setting/carrier-accounts/carrier-forms/create-seko-omni-parcel-carrier-form";
import { SendcloudCarrierDefault } from "@redotech/redo-web/fulfillment/setting/carrier-accounts/carrier-forms/create-sendcloud-carrier-form";
import { SpeeDeeCarrierDefault } from "@redotech/redo-web/fulfillment/setting/carrier-accounts/carrier-forms/create-spee-dee-carrier-form";
import { StallionExpressCarrierDefault } from "@redotech/redo-web/fulfillment/setting/carrier-accounts/carrier-forms/create-stallion-express-carrier-form";
import { UniUniCarrierDefault } from "@redotech/redo-web/fulfillment/setting/carrier-accounts/carrier-forms/create-uniuni-carrier-form";
import {
  upsCarrierDefault,
  upsSurepostCarrierDefault,
} from "@redotech/redo-web/fulfillment/setting/carrier-accounts/carrier-forms/create-ups-carrier-form";
import { upsMailInnovationsCarrierDefault } from "@redotech/redo-web/fulfillment/setting/carrier-accounts/carrier-forms/create-ups-mail-innovations-carrier-form";
import { uspsCarrierDefault } from "@redotech/redo-web/fulfillment/setting/carrier-accounts/carrier-forms/create-usps-carrier-form";
import { uspsDirectCarrierDefault } from "@redotech/redo-web/fulfillment/setting/carrier-accounts/carrier-forms/create-usps-direct-carrier-form";
import { uspsShipCarrierDefault } from "@redotech/redo-web/fulfillment/setting/carrier-accounts/carrier-forms/create-usps-ship-carrier-form";
import { vehoCarrierDefault } from "@redotech/redo-web/fulfillment/setting/carrier-accounts/carrier-forms/create-veho-carrier-form";
import { CarrierInputValue } from "@redotech/redo-web/fulfillment/setting/carrier-accounts/carrier-input";
import { CarriersTable } from "@redotech/redo-web/fulfillment/setting/carrier-accounts/carriers-table";
import { useCarriers } from "@redotech/redo-web/fulfillment/setting/carrier-accounts/use-carriers";
import { memo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { TeamContext } from "../../team";

export const RedoChildCarrierAccountsCard = memo(
  function RedoChildCarrierAccountsCard() {
    // For now, using empty array and MERCHANT type as placeholder
    const [savedCarriers, setSavedCarriers] = useState<
      ICarrierAccount[] | null
    >([]);

    const [createTestLabelPending, setCreateTestLabelPending] = useState(false);

    const redoAdminRpcClient = useRequiredContext(RedoAdminRpcClientContext);
    const team = useRequiredContext(TeamContext);

    const createCarrierAccount = useHandler(
      async (carrier: ICreateCarrierAccount) => {
        return redoAdminRpcClient.createCarrierAccount({
          teamId: team._id.toString(),
          carrierAccount: carrier,
        });
      },
    );

    const deleteCarrierAccount = useHandler(
      async ({ carrierAccount }: { carrierAccount: ICarrierAccount }) => {
        return redoAdminRpcClient.deleteCarrierAccount({
          teamId: team._id.toString(),
          carrierAccount,
        });
      },
    );

    useLoad(async () => {
      const result = await redoAdminRpcClient.getCarrierAccounts({
        teamId: team._id.toString(),
      });
      setSavedCarriers(result.redoChildCarrierAccounts);
    }, []);

    const { addCarrier, deleteCarrier, authenticateCarrier } = useCarriers({
      carrierAccountType: CarrierAccountType.REDO_CHILD,
      setSavedCarriers,
      createCarrierAccount,
      deleteCarrierAccount,
    });

    const createTestLabelHandler = useHandler(
      async (carrierAccount: ICarrierAccount) => {
        setCreateTestLabelPending(true);
        try {
          if (carrierAccount.provider !== RateProvider.EASYPOST) {
            toast(`Cannot create label for ${carrierAccount.name}`, {
              variant: "error",
            });
            return;
          }
          const result = await redoAdminRpcClient.createTestLabel({
            teamId: team._id.toString(),
            carrierAccountId: carrierAccount.carrierAccountId,
          });

          if (result.success) {
            window.open(result.labelUrl, "_blank");
          } else {
            toast(`Failed to create label: ${result.error}`, {
              variant: "error",
            });
          }
        } finally {
          setCreateTestLabelPending(false);
        }
      },
    );

    // After we add a Redo child carrier account, we may have also auto blocked a service level.
    // Refresh the page so that the blocked service level is reflected
    const addRedoChildCarrierAccount = useHandler(
      async (carrier: CarrierInputValue) => {
        const result = await addCarrier(carrier);
        window.location.reload();
        return result;
      },
    );

    const carrierInputDefault: CarrierInputValue = {
      name: "Redo child carrier account",
      redoCarrierName: RedoCarrierName.UNIUNI,
      upsCarrierInput: upsCarrierDefault(),
      upsSurepostCarrierInput: upsSurepostCarrierDefault(),
      upsMailInnovationsCarrierInput: upsMailInnovationsCarrierDefault(),
      dhlCarrierInput: dhlCarrierDefault(),
      dhlExpressCarrierInput: dhlExpressCarrierDefault(),
      uspsCarrierInput: uspsCarrierDefault(),
      fedexCrossBorderCarrierInput: fedexCrossBorderCarrierDefault(),
      fedexCarrierInput: fedexCarrierDefault(),
      fedexSmartpostCarrierInput: fedexSmartpostCarrierDefault(),
      vehoCarrierInput: vehoCarrierDefault(),
      firstMileCarrierInput: firstmileCarrierDefault(),
      australiaPostCarrierInput: australiaPostCarrierDefault(),
      canadaPostCarrierInput: canadaPostCarrierDefault(),
      royalMailCarrierInput: royalMailCarrierDefault(),
      royalMailV3CarrierInput: royalMailV3CarrierDefault(),
      apcCarrierInput: apcCarrierDefault(),
      sekoOmniParcelCarrierInput: sekoOmniParcelCarrierDefault(),
      sendcloudCarrierInput: SendcloudCarrierDefault(),
      glsCarrierInput: glsCarrierDefault(),
      asendiaCarrierInput: asendiaCarrierDefault(),
      speeDeeCarrierInput: SpeeDeeCarrierDefault(),
      amazonCarrierInput: amazonCarrierDefault(),
      uspsShipCarrierInput: uspsShipCarrierDefault(),
      uspsDirectCarrierInput: uspsDirectCarrierDefault(),
      passportGlobalCarrierInput: PassportGlobalCarrierDefault(),
      epostGlobalCarrierInput: EPostGlobalCarrierDefault(),
      etaExpressCarrierInput: ETAExpressCarrierDefault(),
      onTracCarrierInput: OnTracCarrierDefault(),
      purolatorCarrierInput: purolatorCarrierDefault(),
      uniUniCarrierInput: UniUniCarrierDefault(),
      stallionExpressCarrierInput: StallionExpressCarrierDefault(),
      canparCarrierInput: canparCarrierDefault(),
    };

    return (
      <Card title="Create Redo child carrier account">
        <CarriersTable
          carrierAccountType={CarrierAccountType.REDO_CHILD}
          carrierInputDefault={carrierInputDefault}
          createTestLabelHandler={createTestLabelHandler}
          createTestLabelPending={createTestLabelPending}
          handleAddCarrier={addRedoChildCarrierAccount}
          handleAuthenticateCarrier={authenticateCarrier}
          handleDeleteCarrier={deleteCarrier}
          savedCarriers={savedCarriers}
          tableTitle="Redo child carrier accounts"
        />
      </Card>
    );
  },
);
